/* eslint-disable max-classes-per-file */
import { LIKED_VIDEOS_KEY } from '../../../constants/localStorage';
import { PAGE_CHANNEL, PAGE_MAIN, PAGE_PLAYER, PAGE_SEARCH, PAGE_SEARCH_PLAYER } from '../../../constants/pages';
import {
  CHANNEL_LIST_PATH,
  CHANNEL_VIEW_PATH,
  DEFAULT_VIEW_PATH,
  SEARCH_AI_VIEW_PATH,
  SEARCH_PATH,
  SEARCH_VIEW_PATH,
  SHARE_VIEW_PATH,
  SINGLE_VIEW_PATH,
} from '../../../constants/routePath';
import { PLAYLIST_REQUEST_EVENT, X_RAY_CLICK_EVENT } from '../constants/events';
import { HAVE_ENOUGH_DATA } from '@/modules/common/components/Player/constants';

import { createTag, mountTag } from '../../../helpers/dom';
import { getStorage, setStoreParam } from '../../../helpers/localStorage';
import logger from '../../../helpers/logger';
import { deepClone } from '../../../helpers/object';

import { getCustomWidgetId } from './index';
import { generateContent, generateTooltip } from './timelineElement';

class PlayerWidgetInitial {
  constructor({
    playerWidgetUrl,
    publisherSlug,
    playerName,
    offsetTop,
    autoplay,
    sound,
    instanceKey,
    enableFloating,
    aspectRatio,
  }) {
    this.widget = null;
    this.isVisible = false;
    this.isDestroy = false;
    this.externalOptions = {
      enableFloating,
    };

    this.customWidgetId = getCustomWidgetId(instanceKey);
    this.innerCollection = {};
    this.configPlayerProps = {
      autoplayVideo: autoplay,
      playWithSound: sound,
    };

    this.cPThrough = false;
    this.cPMemoCollection = {};

    this.messageSubscribeFn = null;

    const widgetContainerTagName = 'watch-player-container';
    this.hiddenWrapperPlayerDOMContainer = document.querySelector(widgetContainerTagName);

    if (!this.hiddenWrapperPlayerDOMContainer) {
      this.hiddenWrapperPlayerDOMContainer = createTag(widgetContainerTagName);
      this.hiddenWrapperPlayerDOMContainer.style.cssText =
        'position:fixed;top:-100%;left:-100%;visibility:hidden;opacity:0;z-index:-9999';

      document.body.appendChild(this.hiddenWrapperPlayerDOMContainer);
    }

    this.playerContainer = mountTag('div', {
      withoutUnmount: true,
    });

    logger.log('Player script append to DOM');

    mountTag('script', {
      props: {
        src: playerWidgetUrl,
        async: true,
      },
      attrs: {
        pubname: publisherSlug,
        widgetname: playerName,
        'data-floating-pw': offsetTop,
        'data-ar': aspectRatio,
        'data-cr': '0',
        'data-custom-wid': this.customWidgetId,
      },
      appendContainer: this.playerContainer,
      withoutUnmount: true,
    });

    this.hiddenWrapperPlayerDOMContainer.appendChild(this.playerContainer);
  }

  safetyCallApi(methodName) {
    return (...args) => {
      const method = this.widget?.[methodName];
      let fnResult;
      let errorObj = null;

      if (method) {
        try {
          fnResult = method(...args);
        } catch (e) {
          errorObj = e;
        }
      }

      const logParams = [`(Player API) Call method *${methodName}()*`, ...args];

      if (errorObj) {
        logger.error(...logParams, '\n', 'API Was crashed', errorObj);
      } else if (this.widget && !method) {
        logger.warn(...logParams, '\n', 'API Method is not available in the widget', this.widget);
      } else {
        logger.log(...logParams);
      }

      return fnResult;
    };
  }

  play() {
    return this.safetyCallApi('play')();
  }

  pause() {
    return this.safetyCallApi('pause')();
  }

  muted(isMuted) {
    return this.safetyCallApi('muted')(isMuted);
  }

  setCurrentTime(time) {
    return this.safetyCallApi('setCurrentTime')(time);
  }

  chapteringUIEnable(isEnable) {
    return this.safetyCallApi('chapteringUIEnable')(isEnable);
  }

  floatingModeToggle(isEnable) {
    return this.safetyCallApi('floatingModeToggle')(isEnable);
  }

  setUserID(id) {
    return this.safetyCallApi('setUserID')(id);
  }

  disableNext() {
    return this.safetyCallApi('disableNext')();
  }

  goActive() {
    return this.safetyCallApi('goActive')();
  }

  goIdle() {
    return this.safetyCallApi('goIdle')();
  }

  playerMoved() {
    return this.safetyCallApi('playerMoved')();
  }

  sendLike(mediaId, shouldBeLiked) {
    return this.safetyCallApi('sendLike')(mediaId, shouldBeLiked);
  }

  sendShare(provider) {
    return this.safetyCallApi('sendShare')(provider);
  }

  setExternalPlaylistResponse(playlistResponse, configPlayer) {
    return this.safetyCallApi('setExternalPlaylistResponse')(playlistResponse, configPlayer);
  }

  setActiveMark(id) {
    return this.safetyCallApi('setActiveMark')(id);
  }

  setMarks(markList) {
    return this.safetyCallApi('setMarks')(markList);
  }

  setCustomSwitchDefaultState(options) {
    return this.safetyCallApi('setCustomSwitchDefaultState')(options);
  }

  setCustomSwitchState(options) {
    return this.safetyCallApi('setCustomSwitchState')(options);
  }

  forwarde(eventName, options) {
    return this.safetyCallApi('forwarde')(eventName, options);
  }

  setPseudoFS(isEnable) {
    return this.safetyCallApi('setPseudoFS')(isEnable);
  }

  subscribe(eventName, callBack) {
    return this.safetyCallApi('subscribe')(callBack, eventName, null, 1);
  }

  unsubscribe(eventName, callBack) {
    return this.safetyCallApi('unsubscribe')(callBack, eventName);
  }

  destroy() {
    return this.safetyCallApi('destroy')();
  }
}

export default class PlayerWidget extends PlayerWidgetInitial {
  constructor(props) {
    super(props);

    this.firstInit = true;
    this.firstPlaylistInit = true;
    this.selectedMediaId = null;
    this.subscribeBeforeInitStack = [];
    this.playerOnMessageSubscribe();

    [
      {
        method: this.setUserID,
        onlyLastCall: true,
      },
      {
        method: this.setCurrentTime,
        onlyLastCall: true,
      },
      {
        method: this.setCustomSwitchDefaultState,
        onlyLastCall: true,
      },
      {
        method: this.setCustomSwitchState,
        onlyLastCall: true,
      },
      {
        method: this.setMarks,
      },
      {
        method: this.setActiveMark,
      },
      {
        method: this.muted,
        onlyLastCall: true,
      },
      {
        method: this.play,
        onlyLastCall: true,
      },
      {
        method: this.pause,
        onlyLastCall: true,
      },
      {
        method: this.sendLike,
        onlyLastCall: true,
      },
      {
        method: this.sendShare,
      },
      {
        method: this.sendStatsAddComment,
      },
      {
        method: this.sendStatsEditComment,
      },
      {
        method: this.sendStatsDeleteComment,
      },
      {
        method: this.sendStatsAddShare,
      },
      {
        method: this.sendStatsPerformDS,
      },
      {
        method: this.sendStatsPerformWS,
      },
      {
        method: this.sendStatsClipChangeWS,
      },
      {
        method: this.sendStatsGenAiSearch,
      },
      {
        method: this.sendStatsGenAiVideoSlide,
      },
      {
        method: this.sendStatsGenAiVideoOpen,
      },
      {
        method: this.chapteringUIEnable,
      },
    ].forEach(({ method, onlyLastCall = false }) => {
      this[method.name] = this.subscribeBeforeInit(method.name, method.bind(this), onlyLastCall);
    });
  }

  muted(isMuted) {
    this.canPlayThroughSubscribe(super.muted.name, () => super.muted(isMuted));
  }

  play() {
    this.canPlayThroughSubscribe(super.play.name, () => super.play());
  }

  pause() {
    this.canPlayThroughSubscribe(super.pause.name, () => super.pause());
  }

  setCurrentTime(time) {
    this.canPlayThroughSubscribe(super.setCurrentTime.name, () => super.setCurrentTime(time));
  }

  subscribeBeforeInit(fnName, fnSource, onlyLastCall) {
    return (...args) => {
      if (!this.widget) {
        if (onlyLastCall) {
          this.subscribeBeforeInitStack = this.subscribeBeforeInitStack.filter(({ id }) => id !== fnName);
        }

        logger.log('Push subscription into *before init stack*', fnName);
        this.subscribeBeforeInitStack.push({
          id: fnName,
          fn: () => fnSource(...args),
        });
      } else {
        fnSource(...args);
      }
    };
  }

  playerOnMessageSubscribe(callBack) {
    window.removeEventListener('message', this.messageSubscribeFn);

    this.messageSubscribeFn = (event) => {
      if (event.origin === window.location.origin) {
        const reg = /lre:playerReady:\/\//;

        if (reg.test(event.data)) {
          const { sessionId } = JSON.parse(event.data.replace(reg, ''));
          const widget = window.anyclip.getWidget(null, sessionId);

          if (widget && widget.customWidgetId === this.customWidgetId) {
            logger.log('Widget LRE Player was initialized', widget);
            this.widget = widget;

            if (!this.isDestroy) {
              this.disableNext();
              this.isVisible = true;

              if (this.subscribeBeforeInitStack.length) {
                logger.log(
                  'Run subscription from *before init stack*',
                  this.subscribeBeforeInitStack.map(({ id }) => id).join(', '),
                );

                this.subscribeBeforeInitStack.forEach(({ fn }) => fn());
                this.subscribeBeforeInitStack = [];
              }

              if (callBack) {
                callBack();
                this.floatingModeToggle(this.externalOptions.enableFloating);
              } else {
                this.floatingModeToggle(false);
                this.goIdle();
              }
            }
          }
        }
      }
    };

    window.addEventListener('message', this.messageSubscribeFn);
  }

  reInit(options) {
    logger.debug('LWPlayerWidget: reInit');
    if (this.widget) {
      this.init(options);
    } else {
      this.playerOnMessageSubscribe(() => this.init(options));
    }
  }

  subscribe(event, callBack) {
    super.subscribe(event, callBack);

    this.innerCollection[event] = callBack;
  }

  unsubscribe(event, callback) {
    super.unsubscribe(event, callback);

    delete this.innerCollection[event];
  }

  canPlayThroughSubscribe(memoUidKey, callBack) {
    const on = (event, callBack$) => {
      try {
        this.widget.player.on(event, callBack$);
      } catch (e) {
        logger.error('(Player API) Call method *player.on()*', event, callBack$, '\n', 'API Was crashed', e);
      }
    };

    const off = (event, callBack$) => {
      try {
        this.widget.player.off(event, callBack$);
      } catch (e) {
        logger.error('(Player API) Call method *player.off()*', event, callBack$, '\n', 'API Was crashed', e);
      }
    };

    if (this.cPMemoCollection[memoUidKey]) {
      off('canplaythrough', this.cPMemoCollection[memoUidKey]);
      this.cPMemoCollection[memoUidKey] = null;
    }

    const readyState = () => {
      try {
        return this.widget.player?.readyState();
      } catch (e) {
        logger.error('(Player API) Call method *player.readyState()*', '\n', 'API Was crashed', e);
        return 0;
      }
    };

    if (!this.cPThrough && readyState() < HAVE_ENOUGH_DATA) {
      this.cPMemoCollection[memoUidKey] = () => {
        this.cPThrough = true;

        off('canplaythrough', this.cPMemoCollection[memoUidKey]);

        callBack();
      };

      on('canplaythrough', this.cPMemoCollection[memoUidKey]);
    } else {
      callBack();
    }
  }

  canPlayThroughUnsubscribe() {
    const off = (event, callBack$) => {
      try {
        this.widget.player.off(event, callBack$);
      } catch (e) {
        logger.error('(Player API) Call method *player.off()*', event, callBack$, '\n', 'API Was crashed', e);
      }
    };

    Object.entries(this.cPMemoCollection).forEach(([key, fn]) => {
      if (fn) {
        off('canplaythrough', fn);
      }

      delete this.cPMemoCollection[key];
    });

    this.cPThrough = false;
  }

  init(props) {
    logger.debug('LWPlayerWidget: initPlayer');

    this.movePlayer(props.container);

    if (this.firstInit) {
      this.goActive();

      if (this.externalOptions.enableFloating) {
        this.floatingModeToggle(true);
      }
    }

    if (props.video.mediaid !== this.selectedMediaId) {
      if (this.firstPlaylistInit && this.selectedMediaId === null) {
        this.firstPlaylistInit = false;
      } else {
        this.canPlayThroughUnsubscribe();
      }

      this.selectedMediaId = props.video.mediaid;

      const playlistResponse = {
        ...Object.keys(props.playerPropsPassThrow || {}).reduce((acc, key) => {
          const value = props.playerPropsPassThrow[key];

          if (value !== null) {
            acc[key] = value;
          }

          return acc;
        }, {}),
        clipSource: props.clipSource,
        playlist: [deepClone(props.video)],
        plInitialIndex: 0,
      };

      this.setExternalPlaylistResponse(playlistResponse, this.configPlayerProps);

      delete this.configPlayerProps.playWithSound;

      this.canPlayThroughSubscribe('init', () => null);

      Object.entries(this.innerCollection).forEach(([event, callBack]) => this.unsubscribe(event, callBack));

      Object.entries(props.listeners).forEach(([key, subscribeFn]) => {
        if (key === X_RAY_CLICK_EVENT) {
          this.subscribe(X_RAY_CLICK_EVENT, () => {
            const fn = (event) => {
              this.unsubscribe(PLAYLIST_REQUEST_EVENT, fn);

              subscribeFn(event);
            };

            this.subscribe(PLAYLIST_REQUEST_EVENT, fn);
          });
        } else {
          this.subscribe(key, subscribeFn);
        }
      });
    }

    this.firstInit = false;
  }

  movePlayer(sourceContainer) {
    if (!sourceContainer.contains(this.playerContainer)) {
      sourceContainer.appendChild(this.playerContainer);
      logger.log('playerMoved', 'sourceContainer', sourceContainer, 'playerContainer', this.playerContainer);
      this.playerMoved();

      return true;
    }

    return false;
  }

  unmountPlayerContainer() {
    logger.debug('LWPlayerWidget: unmount');

    if (!this.isDestroy) {
      this.movePlayer(this.hiddenWrapperPlayerDOMContainer);

      this.canPlayThroughUnsubscribe();

      if (this.widget) {
        logger.debug('LWPlayerWidget: unmount LRE Player');
        Object.entries(this.innerCollection).forEach(([event, callBack]) => this.unsubscribe(event, callBack));
        this.firstInit = true;
        this.selectedMediaId = null;
        this.floatingModeToggle(false);
        this.goIdle();
      } else {
        this.playerOnMessageSubscribe();
      }
    }
  }

  getCurrentTime() {
    return Math.floor(this.widget?.player?.currentTime?.() || 0) * 1000;
  }

  // eslint-disable-next-line class-methods-use-this
  isCurrentVideoLiked(mediaId) {
    return (getStorage()[LIKED_VIDEOS_KEY] || []).includes(mediaId);
  }

  sendLike(mediaId, shouldBeLiked) {
    const likedVideos = getStorage()[LIKED_VIDEOS_KEY] || [];

    const storageData = shouldBeLiked
      ? likedVideos.concat(mediaId)
      : likedVideos.filter((mediaId$) => mediaId$ !== mediaId);

    setStoreParam(LIKED_VIDEOS_KEY, storageData);

    return super.sendLike(shouldBeLiked);
  }

  setMarks(markList) {
    return super.setMarks(
      markList.map((mark) => ({
        id: mark.id,
        time: mark.time,
        timeEnd: mark.timeEnd,
        chainPlay: mark.chainPlay,
        type: mark.type,
        content: generateContent({ ...mark }),
        tooltipContent: generateTooltip({ ...mark }),
      })),
    );
  }

  /**
   * @param level {1|2}
   * @param index {Number}
   * @param charCount {Number}
   * @param hasMentions {Boolean}
   * @param hasLinks {Boolean}
   */
  sendStatsAddComment(level, index, charCount, hasMentions, hasLinks) {
    return this.forwarde('addComment', {
      level,
      index,
      charCount,
      hasMentions,
      hasLinks,
    });
  }

  /**
   * @param level {1|2}
   * @param index {Number}
   * @param charCount {Number}
   * @param hasMentions {Boolean}
   * @param hasLinks {Boolean}
   */
  sendStatsEditComment(level, index, charCount, hasMentions, hasLinks) {
    return this.forwarde('editComment', {
      level,
      index,
      charCount,
      hasMentions,
      hasLinks,
    });
  }

  /**
   * @param level {1|2}
   * @param index {Number}
   * @param charCount {Number}
   * @param hasMentions {Boolean}
   * @param hasLinks {Boolean}
   */
  sendStatsDeleteComment(level, index, charCount, hasMentions, hasLinks) {
    return this.forwarde('deleteComment', {
      level,
      index,
      charCount,
      hasMentions,
      hasLinks,
    });
  }

  /**
   * @param from {Number}
   * @param charCount {Number}
   * @param userCount {Number}
   */
  sendStatsAddShare(from, charCount, userCount) {
    return this.forwarde('addShare', {
      from,
      charCount,
      userCount,
    });
  }

  /**
   * @param query
   * @param duration
   */
  sendStatsPerformDS(query, duration) {
    return this.forwarde('performDS', {
      query,
      duration,
    });
  }

  /**
   * @param query
   * @param duration
   * @param totalCount
   */
  sendStatsPerformWS(query, duration, totalCount) {
    return this.forwarde('performWS', {
      query,
      duration,
      totalCount,
    });
  }

  /**
   * @param query
   * @param index
   */
  sendStatsClipChangeWS(query, index) {
    return this.canPlayThroughSubscribe('sendStatsClipChangeWS', () =>
      this.forwarde('clipChangeWS', {
        query,
        index,
      }),
    );
  }

  /**
   * @param id
   * @param query
   * @param duration
   * @param videoIds
   * @param totalCount
   * @param watchId
   */
  sendStatsGenAiSearch(id, query, duration, videoIds, totalCount, watchId) {
    return this.forwarde('performGenAiSearch', {
      id,
      query,
      duration,
      videoIds,
      totalCount,
      watchId,
    });
  }

  /**
   * @param id
   * @param direction
   */
  sendStatsGenAiVideoSlide(id, direction) {
    return this.forwarde('performGenAiVideoSlide', {
      id,
      direction,
    });
  }

  /**
   * @param id
   * @param videoId
   * @param videoIndex
   */
  sendStatsGenAiVideoOpen(id, videoId, videoIndex) {
    return this.forwarde('performGenAiVideoOpen', {
      id,
      videoId,
      videoIndex,
    });
  }

  /**
   * @param watchId {Number}
   * @param route {String}
   * @param channelId {String}
   * @param searchQuery {String}
   * @param chip {String}
   * @param chipIndex {Number}
   * @param chipOrigin {String} ["LABEL_NAME", "LABEL_VALUE", "PEOPLE", "BRANDS", "IAB", "KEYWORDS"].join('|')
   */
  sendStatsChipClicked(watchId, route, channelId, searchQuery, chip, chipIndex, chipOrigin) {
    const routePageMapper = {
      [CHANNEL_LIST_PATH]: PAGE_MAIN,
      [CHANNEL_VIEW_PATH]: PAGE_CHANNEL,
      [DEFAULT_VIEW_PATH]: PAGE_PLAYER,
      [SHARE_VIEW_PATH]: PAGE_PLAYER,
      [SINGLE_VIEW_PATH]: PAGE_PLAYER,
      [SEARCH_AI_VIEW_PATH]: PAGE_PLAYER,
      [SEARCH_PATH]: PAGE_SEARCH,
      [SEARCH_VIEW_PATH]: PAGE_SEARCH_PLAYER,
    };

    return this.forwarde('chipClicked', {
      watchId,
      page: routePageMapper[route],
      channelId,
      searchQuery,
      chip,
      chipIndex,
      chipOrigin,
    });
  }

  /**
   * @param eventName
   * @param options
   */
  sendCustomEvent(eventName, options) {
    return this.forwarde(eventName, options);
  }

  /**
   * @param {Boolean} isEnable
   */
  setPseudoFullScreen(isEnable) {
    return this.setPseudoFS(isEnable);
  }

  destroy() {
    this.isDestroy = true;
    this.messageSubscribeFn = null;

    const destroy$ = () => {
      super.destroy();
      this.playerContainer.remove();
    };

    if (this.widget) {
      destroy$();
    } else {
      const messageSubscribeFn = (event) => {
        if (event.origin === window.location.origin) {
          const reg = /lre:playerReady:\/\//;

          if (reg.test(event.data)) {
            const { sessionId } = JSON.parse(event.data.replace(reg, ''));
            const widget = window.anyclip.getWidget(null, sessionId);

            if (widget && widget.customWidgetId === this.customWidgetId) {
              logger.log('Widget LRE Player was initialized', widget);
              this.widget = widget;

              window.removeEventListener('message', messageSubscribeFn);

              destroy$();
            }
          }
        }
      };

      window.addEventListener('message', messageSubscribeFn);
    }
  }
}
